import { useState, useCallback, useMemo } from 'react';
import { IWizardStep, IWizardNavigation, IWizardData } from './types';

interface IUseWizardStateProps<TData = any> {
  steps: IWizardStep<TData>[];
  initialData?: Partial<TData>;
  validateOnNavigation?: boolean;
}

export const useWizardState = <TData = any>({
  steps,
  initialData = {},
  validateOnNavigation = false,
}: IUseWizardStateProps<TData>) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [wizardData, setWizardData] = useState<TData>(initialData as TData);
  const [stepData, setStepData] = useState<Record<string, any>>({});

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;

  const updateData = useCallback((newData: Partial<TData>) => {
    setWizardData((prev) => ({ ...prev, ...newData }));
  }, []);

  const resetData = useCallback(() => {
    setWizardData(initialData as TData);
    setStepData({});
  }, [initialData]);

  const getStepData = useCallback(
    (stepId: string) => {
      return stepData[stepId];
    },
    [stepData],
  );

  const setStepDataCallback = useCallback((stepId: string, data: any) => {
    setStepData((prev) => ({ ...prev, [stepId]: data }));
  }, []);

  const validateStep = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      const step = steps[stepIndex];
      if (!step?.validate) return true;

      try {
        return await step.validate(wizardData);
      } catch {
        return false;
      }
    },
    [steps, wizardData],
  );

  const canGoNext = useMemo(() => {
    return currentStepIndex < totalSteps - 1;
  }, [currentStepIndex, totalSteps]);

  const canGoPrevious = useMemo(() => {
    return currentStepIndex > 0;
  }, [currentStepIndex]);

  const goNext = useCallback(async () => {
    if (!canGoNext) return;

    if (validateOnNavigation) {
      const isValid = await validateStep(currentStepIndex);
      if (!isValid) return;
    }

    setCurrentStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
  }, [canGoNext, validateOnNavigation, validateStep, currentStepIndex, totalSteps]);

  const goPrevious = useCallback(() => {
    if (!canGoPrevious) return;
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, [canGoPrevious]);

  const goToStep = useCallback(
    async (stepIndex: number) => {
      if (stepIndex < 0 || stepIndex >= totalSteps) return;
      if (stepIndex === currentStepIndex) return;

      // If going forward and validation is enabled, validate all steps in between
      if (validateOnNavigation && stepIndex > currentStepIndex) {
        for (let i = currentStepIndex; i < stepIndex; i++) {
          const isValid = await validateStep(i);
          if (!isValid) return;
        }
      }

      setCurrentStepIndex(stepIndex);
    },
    [totalSteps, currentStepIndex, validateOnNavigation, validateStep],
  );

  const skipStep = useCallback(() => {
    if (!currentStep?.optional) return;
    goNext();
  }, [currentStep, goNext]);

  const navigation: IWizardNavigation = useMemo(
    () => ({
      currentStep: currentStepIndex,
      totalSteps,
      canNext: canGoNext,
      canPrevious: canGoPrevious,
      goNext,
      goPrevious,
      goToStep,
      skipStep,
    }),
    [
      currentStepIndex,
      totalSteps,
      canGoNext,
      canGoPrevious,
      goNext,
      goPrevious,
      goToStep,
      skipStep,
    ],
  );

  const data: IWizardData<TData> = useMemo(
    () => ({
      data: wizardData,
      updateData,
      resetData,
      getStepData,
      setStepData: setStepDataCallback,
    }),
    [wizardData, updateData, resetData, getStepData, setStepDataCallback],
  );

  return {
    currentStep,
    currentStepIndex,
    navigation,
    data,
    validateStep,
  };
};
